/**
 * PlaceholderRenderer — renders GameObjects without visual content as
 * solid-color rectangles with centered text labels.
 *
 * Each GameObjectType gets a distinct color so users can quickly identify
 * what kind of object they are looking at in the scene view.
 */
import { Container, Graphics, Text, TextStyle } from 'pixi.js';
import type { GameObject } from '@core/types/gameobject.types';

export class PlaceholderRenderer {
  /** Color mapping by GameObjectType. */
  private static readonly TYPE_COLORS: Record<string, number> = {
    container: 0x3b82f6, // blue
    sprite: 0x22c55e, // green
    spine: 0x7c3aed, // purple
    'cascade-slot': 0xf59e0b, // amber
    'backend-slot-game': 0xf59e0b, // amber
    graphics: 0xec4899, // pink
    default: 0x71717a, // gray
  };

  /** Create a PixiJS container with a colored rectangle and name label. */
  static create(gameObject: GameObject, width?: number, height?: number): Container {
    const container = new Container();

    // Determine size
    const w = width || PlaceholderRenderer.resolveWidth(gameObject);
    const h = height || PlaceholderRenderer.resolveHeight(gameObject);

    // Get color based on type
    const color =
      PlaceholderRenderer.TYPE_COLORS[gameObject.type] ??
      PlaceholderRenderer.TYPE_COLORS.default;

    // Draw rounded rectangle background centered on origin
    const bg = new Graphics();
    bg.roundRect(-w / 2, -h / 2, w, h, 4);
    bg.fill({ color, alpha: 0.15 });
    bg.stroke({ color, alpha: 0.4, width: 1 });
    container.addChild(bg);

    // Add name label (centered, white, small font)
    const label = new Text({
      text: gameObject.name,
      style: new TextStyle({
        fontFamily: 'Inter, sans-serif',
        fontSize: Math.min(11, Math.max(8, w / 10)),
        fill: '#ffffff',
        align: 'center',
      }),
    });
    label.anchor.set(0.5, 0.5);

    // Scale down if text overflows the rectangle
    if (label.width > w - 8) {
      label.scale.set(Math.min(1, (w - 8) / label.width));
    }

    container.addChild(label);
    return container;
  }

  /** Update an existing placeholder container with new game object state. */
  static update(
    container: Container,
    gameObject: GameObject,
    width?: number,
    height?: number
  ): void {
    const w = width || PlaceholderRenderer.resolveWidth(gameObject);
    const h = height || PlaceholderRenderer.resolveHeight(gameObject);

    const color =
      PlaceholderRenderer.TYPE_COLORS[gameObject.type] ??
      PlaceholderRenderer.TYPE_COLORS.default;

    // Rebuild the background graphic
    const bg = container.children.find(c => c instanceof Graphics) as Graphics | undefined;
    if (bg) {
      bg.clear();
      bg.roundRect(-w / 2, -h / 2, w, h, 4);
      bg.fill({ color, alpha: 0.15 });
      bg.stroke({ color, alpha: 0.4, width: 1 });
    }

    // Update the label text
    const label = container.children.find(c => c instanceof Text) as Text | undefined;
    if (label) {
      if (label.text !== gameObject.name) {
        label.text = gameObject.name;
      }
      // Recalculate font size and scale for new dimensions
      label.style.fontSize = Math.min(11, Math.max(8, w / 10));
      label.scale.set(1);
      if (label.width > w - 8) {
        label.scale.set(Math.min(1, (w - 8) / label.width));
      }
    }
  }

  /** Resolve width from component data or type-based defaults. */
  private static resolveWidth(gameObject: GameObject): number {
    // Check for customWidth in cascade-slot-part component
    const partComp = gameObject.components.find(c => c.type === 'cascade-slot-part');
    if (partComp?.data?.customWidth && (partComp.data.customWidth as number) > 0) {
      return partComp.data.customWidth as number;
    }

    // Check sprite-renderer for textureWidth
    const spriteComp = gameObject.components.find(c => c.type === 'sprite-renderer');
    if (spriteComp?.data?.textureWidth && (spriteComp.data.textureWidth as number) > 0) {
      return spriteComp.data.textureWidth as number;
    }

    // Type-based defaults
    switch (gameObject.type) {
      case 'backend-slot-game':
      case 'cascade-slot':
        return 720;
      case 'sprite':
        return 80;
      case 'text':
        return 120;
      case 'spine':
        return 100;
      default:
        return 100;
    }
  }

  /** Resolve height from component data or type-based defaults. */
  private static resolveHeight(gameObject: GameObject): number {
    const partComp = gameObject.components.find(c => c.type === 'cascade-slot-part');
    if (partComp?.data?.customHeight && (partComp.data.customHeight as number) > 0) {
      return partComp.data.customHeight as number;
    }

    const spriteComp = gameObject.components.find(c => c.type === 'sprite-renderer');
    if (spriteComp?.data?.textureHeight && (spriteComp.data.textureHeight as number) > 0) {
      return spriteComp.data.textureHeight as number;
    }

    switch (gameObject.type) {
      case 'backend-slot-game':
      case 'cascade-slot':
        return 1280;
      case 'sprite':
        return 80;
      case 'text':
        return 30;
      case 'spine':
        return 100;
      default:
        return 60;
    }
  }
}
